<template>
	<NavBar />
	<BackgroundImage
		:imageUrl="require('@/assets/login/bg.png')"
		:customStyle="{ minHeight: '100vh' }"
	>
		<div class="pill-wrapper">
			<a-spin :spinning="pageLoading">
				<div class="layout">
					<div class="info-column">
						<a-card class="info-card" :bordered="false">
							<div class="info-header">
								<img :src="moduleInfo.avatar" :alt="moduleInfo.name" class="info-avatar" />
								<div class="info-meta">
									<h1 class="info-name">{{ moduleInfo.name }}</h1>
									<p class="info-subtitle">专药智能体 · 中药方剂智能推荐助手</p>
								</div>
							</div>

							<div class="tag-list">
								<a-tag v-for="tag in moduleInfo.tags" :key="tag.tag_id" color="blue">{{ tag.name }}</a-tag>
							</div>

							<div class="section-tabs">
								<button
									class="tab-btn"
									:class="{ active: activeTab === 'intro' }"
									type="button"
									@click="activeTab = 'intro'"
								>
									功能简介
								</button>
								<button
									class="tab-btn"
									:class="{ active: activeTab === 'summary' }"
									type="button"
									@click="activeTab = 'summary'"
								>
									模型说明
								</button>
							</div>

							<p class="info-description">{{ currentDescription }}</p>

							<div class="more-info">
								<span>了解更多专药模型信息</span>
								<RightOutlined />
							</div>
						</a-card>

						<a-card class="question-card" :bordered="false">
							<div class="question-header">
								<h3>您可能还想问</h3>
								<p>一键带入对话，获得个性化答复</p>
							</div>
							<div class="question-list">
								<a-button
									v-for="item in quickQuestions"
									:key="item"
									class="question-item"
									block
									shape="round"
									@click="handleQuestionClick(item)"
								>
									<template #icon>
										<MessageOutlined />
									</template>
									{{ item }}
								</a-button>
							</div>
						</a-card>
					</div>

					<div class="chat-column">
						<a-card class="chat-card" :bordered="false">
							<div class="chat-header">
								<div>
									<h2>专药智能对话</h2>
									<p>围绕中药材、方剂、体质匹配与用药建议提供实时咨询</p>
								</div>
								<div class="chat-header-right">
									<a-button type="text" class="history-btn" @click="showHistory">
										<template #icon><HistoryOutlined /></template>
										历史会话
									</a-button>
									<a-tag color="purple">AI 中药助手</a-tag>
								</div>
							</div>

							<div ref="chatBody" class="chat-body">
								<div v-if="!chatMessages.length && !chatLoading" class="chat-empty">
									<p>开始提问，AI 将结合中医药知识为您提供用药建议。</p>
									<a-button type="link" @click="handleQuestionClick(quickQuestions[0])">试试快速提问</a-button>
								</div>

							<div
								v-for="(item, index) in chatMessages"
								:key="index"
								:class="['chat-message', item.role]"
							>
								<div class="bubble">
									<div v-if="item.reasoning" class="reasoning-section">
										<div class="reasoning-header" @click="toggleReasoning(index)">
											<span class="reasoning-title">🤔 AI 思考过程</span>
											<span class="reasoning-toggle">{{ item.reasoningCollapsed ? '展开' : '收起' }}</span>
										</div>
										<div v-show="!item.reasoningCollapsed" class="reasoning-content">
											<p class="chat-reason">{{ item.reasoning }}</p>
										</div>
									</div>
									<div v-if="item.content" class="chat-text" v-html="parseMarkdown(item.content)"></div>
									<div v-if="item.loading" class="chat-loading">
										<a-spin size="small" />
										<span>AI 正在思考...</span>
									</div>
									<div v-if="item.error" class="chat-error">未获取到内容，请稍后重试。</div>
								</div>
							</div>
							</div>

							<div class="chat-input">
								<a-textarea
									v-model:value="chatInput"
									:auto-size="{ minRows: 2, maxRows: 4 }"
									placeholder="请输入您想咨询的问题，支持换行"
									:disabled="chatLoading"
									@pressEnter="handlePressEnter"
								/>
								<div class="chat-actions">
									<a-button type="primary" shape="round" :loading="chatLoading" @click="handleSend">
										发送
									</a-button>
								</div>
								<p class="chat-tip">内容由 AI 生成，仅供参考</p>
							</div>
						</a-card>
					</div>
				</div>
			</a-spin>
		</div>

		<a-drawer
			v-model:visible="historyVisible"
			title="历史会话"
			placement="right"
			width="320px"
			:bodyStyle="{ padding: '0' }"
		>
			<div class="topic-content">
				<a-spin :spinning="historyLoading">
					<div v-if="!historyList.length && !historyLoading" class="history-empty">
						暂无历史会话
					</div>
					<div v-for="(group, index) in historyList" :key="index" class="topic-group" >
						<p class="title">{{ group.timeGroup }}</p>
						<div
							v-for="item in group.list"
							:key="item.topic_id"
							class="topic"
							:class="{ active: sessionId == item.topic_id }"
							@click="selectHistory(item)"
						>
							<span>{{ item.topic || item.request_content }}</span>
							
							<a-popconfirm
								title="确定删除该会话吗？"
								ok-text="是"
								cancel-text="否"
								@confirm="(e) => deleteHistory(item, e)"
								@click.stop
							>
								<a-button type="text" class="delete-btn" @click.stop>
									<DeleteOutlined />
								</a-button>
							</a-popconfirm>
						</div>
					</div>
				</a-spin>
			</div>
		</a-drawer>
	</BackgroundImage>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import BackgroundImage from '@/components/BackgroundImage.vue'
import { MessageOutlined, RightOutlined, HistoryOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { XStream } from 'ant-design-x-vue'
import api from '@/api'
import { message } from 'ant-design-vue'
import { marked } from 'marked'
import katex from 'katex'
import 'katex/dist/katex.min.css'

const DEFAULT_AVATAR = 'https://qihuang.oss-cn-qingdao.aliyuncs.com/images/doctor/default.png'

export default {
	name: 'PillPage',
	components: {
		NavBar,
		BackgroundImage,
		MessageOutlined,
		RightOutlined,
		HistoryOutlined,
		DeleteOutlined
	},
	data() {
		return {
			pageLoading: true,
			moduleInfo: {
				id: 4,
				name: '专药智能模型',
				avatar: DEFAULT_AVATAR,
				tags: [],
				intro: '专药智能模型，提供中药材、方剂知识查询与个性化用药推荐。',
				summary: '模型结合中医药知识图谱与体质辨识，为用户提供科学、安全的用药建议。'
			},
			activeTab: 'intro',
			chatMessages: [],
			chatInput: '',
			chatLoading: false,
			sessionId: '0',
			quickQuestions: [
				'阴虚体质适合服用哪些中药调理？',
				'六味地黄丸的功效与适用人群是什么？',
				'失眠多梦可以用哪些方剂调理？'
			],
			historyVisible: false,
			historyList: [],
			historyLoading: false
		}
	},
	computed: {
		currentDescription() {
			return this.activeTab === 'intro' ? this.moduleInfo.intro : this.moduleInfo.summary
		}
	},
	mounted() {
		this.initPage()
	},
	methods: {
		async initPage() {
			try {
				await this.fetchModuleDetail()
			} catch (error) {
				console.error('初始化失败:', error)
				message.error('加载专药助手信息失败，请稍后再试')
			} finally {
				this.pageLoading = false
			}
		},
		async fetchModuleDetail() {
			try {
				const response = await api.pill.getModuleDetail({ doctor_id: this.$route.query.id })
				if (response?.code === 1 && response.data) {
					const data = response.data
					this.moduleInfo = {
						id: data.doctor_id || this.moduleInfo.id,
						name: data.name || this.moduleInfo.name,
						avatar: data.more?.thumbnail || DEFAULT_AVATAR,
						tags: Array.isArray(data.tag) ? data.tag : [],
						intro: data.bio || this.moduleInfo.intro,
						summary: data.expertise || this.moduleInfo.summary
					}
				}
			} catch (error) {
				console.error('获取模型详情失败:', error)
			}
		},
		handleQuestionClick(question) {
			this.handleSend(question)
		},
		handleSend(content) {
			const text = typeof content === 'string' ? content.trim() : this.chatInput.trim()
			if (!text) {
				return
			}
			if (this.chatLoading) {
				message.info('AI 正在回答，请稍候...')
				return
			}
			this.chatInput = ''
			this.pushUserMessage(text)
			this.streamAssistantReply(text)
		},
		handlePressEnter(event) {
			if (!event.shiftKey) {
				event.preventDefault()
				this.handleSend()
			}
		},
		pushUserMessage(content) {
			this.chatMessages.push({
				role: 'user',
				content
			})
			this.$nextTick(this.scrollChatToBottom)
		},
		async streamAssistantReply(content) {
			const initialMessage = {
				role: 'assistant',
				content: '',
				reasoning: '',
				reasoningCollapsed: false,
				reasoningComplete: false,
				loading: true,
				error: false,
				rawContent: '' // 用于累积原始内容
			}
			this.chatMessages.push(initialMessage)
			const assistantMessage = this.chatMessages[this.chatMessages.length - 1]

			const stopMessageLoadingIfNeeded = () => {
				if (
					assistantMessage.loading &&
					((assistantMessage.reasoning && assistantMessage.reasoning.trim()) ||
						(assistantMessage.content && assistantMessage.content.trim()))
				) {
					assistantMessage.loading = false
				}
			}

			this.chatLoading = true
			this.$nextTick(this.scrollChatToBottom)

			try {
				const response = await api.pill.streamChat({
					query: content,
					topic_id: this.sessionId,
					doctor_id: this.moduleInfo.id
				})

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`)
				}

				for await (const chunk of XStream({
					readableStream: response.body,
				})) {
					if (chunk.data) {
						const partData = chunk.data
						if (partData.trim() === '[DONE]') {
							assistantMessage.loading = false
							break
						}

					try {
						const data = JSON.parse(partData)

						if (data.answer) {
							// 累积原始内容
							assistantMessage.rawContent += data.answer
							
							// 处理累积的内容
							let processedContent = assistantMessage.rawContent
							const thinkStartIndex = processedContent.indexOf('<think>')
							const thinkEndIndex = processedContent.indexOf('</think>')

						if (thinkStartIndex !== -1 && thinkEndIndex !== -1 && thinkEndIndex > thinkStartIndex) {
							// 提取思考内容
							const thinkContent = processedContent.substring(thinkStartIndex + 7, thinkEndIndex)
							assistantMessage.reasoning = thinkContent.replace(/\\n/g, '\n').trim()
							
							// reasoning 完整了,自动折叠
							if (!assistantMessage.reasoningComplete) {
								assistantMessage.reasoningComplete = true
								assistantMessage.reasoningCollapsed = true
							}
							
							// 移除 <think>...</think> 部分
							processedContent =
								processedContent.substring(0, thinkStartIndex) + processedContent.substring(thinkEndIndex + 8)
						} else if (thinkStartIndex !== -1) {
								// 只找到开始标签，提取已有的思考内容
								const partialThinkContent = processedContent.substring(thinkStartIndex + 7)
								assistantMessage.reasoning = partialThinkContent.replace(/\\n/g, '\n').trimStart()
								// 暂时只显示标签前的内容
								processedContent = processedContent.substring(0, thinkStartIndex)
							}
							
							assistantMessage.content = processedContent.trimStart()
							
							// 只要有内容就关闭 loading
							stopMessageLoadingIfNeeded()
							
							this.$nextTick(this.scrollChatToBottom)
						}

						// Some backends return `topic_id`, some return `id` for the created topic.
						// Accept either so `sessionId` is properly populated for follow-up messages.
						if (data.conversation_id) {
							const tid = data.conversation_id
							this.sessionId = `${tid}`
						}
					} catch (e) {
						console.warn('Parse stream data error:', e, partData)
					}
					}
				}

				assistantMessage.loading = false
			} catch (error) {
				console.error('对话失败:', error)
				assistantMessage.error = true
				assistantMessage.loading = false
				assistantMessage.content = assistantMessage.content || '对话请求失败，请稍后再试'
				message.error('对话失败，请稍后再试')
			} finally {
				this.chatLoading = false
				this.$nextTick(this.scrollChatToBottom)
			}
		},
		scrollChatToBottom() {
			const container = this.$refs.chatBody
			if (container) {
				container.scrollTop = container.scrollHeight
			}
		},
		toggleReasoning(index) {
			const message = this.chatMessages[index]
			if (message && message.reasoning) {
				message.reasoningCollapsed = !message.reasoningCollapsed
			}
		},
		//将话题 按时间分组
		groupByTime(data) {
			// 1. 根据 create_time_str 分组
			const map = data.reduce((acc, item) => {
				const timeGroup = item.create_time_str || '未分组'
				if (!acc[timeGroup]) {
					acc[timeGroup] = []
				}
				acc[timeGroup].push(item)
				return acc
			}, {})

			// 2. 生成有序数组，并且按照 create_time 时间戳倒序排序
			const orderedGroups = Object.keys(map)
				.sort((a, b) => {
					// 比较 create_time 字段来确定时间顺序
					const latestA = map[a].reduce((max, item) => Math.max(max, item.create_time), 0)
					const latestB = map[b].reduce((max, item) => Math.max(max, item.create_time), 0)
					return latestB - latestA // 倒序：最新的在前面
				})
				.map((timeGroup) => {
					const sortedList = map[timeGroup].sort((a, b) => b.create_time - a.create_time) // 组内时间戳排序
					return {
						timeGroup,
						list: sortedList
					}
				})

			return orderedGroups
		},
		async showHistory() {
			this.historyVisible = true
			this.fetchHistory()
		},
		async fetchHistory() {
			this.historyLoading = true
			try {
				const res = await api.pill.getTopicList({
					doctor_id: this.moduleInfo.id,
					page: 1,
					pageSize: 20
				})
				if (res.code === 1) {
					this.historyList = this.groupByTime(res.data.data || [])
				}
			} catch (e) {
				console.error(e)
				message.error('获取历史会话失败')
			} finally {
				this.historyLoading = false
			}
		},
		async deleteHistory(item, e) {
			e.stopPropagation()
			try {
				const res = await api.pill.deleteTopic({
					topic_id: item.topic_id
				})
				if (res.code === 1) {
					message.success('删除成功')
					this.fetchHistory()
					if (this.sessionId == item.topic_id) {
						this.sessionId = '0'
						this.chatMessages = []
					}
				}
			} catch (e) {
				message.error('删除失败')
			}
		},
		async selectHistory(item) {
			this.sessionId = item.topic_id
			this.historyVisible = false
			this.chatLoading = true
			this.chatMessages = []
			try {
				const res = await api.pill.getTopicDetail({
					topic_id: item.topic_id,
					page: 1,
					pageSize: 100
				})
				if (res.code === 1) {
					// topicDetail returns a list directly usually, or check if it is paginated
					// Based on wxapp code, it seems to be an array in res.data
					// But let's handle both cases just in case
					const list = Array.isArray(res.data) ? res.data : (res.data?.data || [])
					
					const messages = []
					// list is usually ordered by time asc or desc? 
					// Assuming it's chronological or we need to sort.
					// Usually API returns chronological for chat history.
					
					list.forEach(item => {
						// User message
						messages.push({
							role: 'user',
							content: item.request_content
						})
						
						// Assistant message(s)
						if (item.response && item.response.length) {
							item.response.forEach(resp => {
								let content = resp.response_content
								let reasoning = resp.reasoning_content || ''
								
								// Parse <think> if reasoning is empty and content has it
								if (!reasoning && content && content.includes('<think>')) {
									const thinkStart = content.indexOf('<think>')
									const thinkEnd = content.indexOf('</think>')
									if (thinkStart !== -1 && thinkEnd !== -1) {
										reasoning = content.substring(thinkStart + 7, thinkEnd).trim()
										content = content.substring(0, thinkStart) + content.substring(thinkEnd + 8)
									}
								}
								
								messages.push({
									role: 'assistant',
									content: content,
									reasoning: reasoning,
									reasoningCollapsed: true,
									reasoningComplete: true,
									loading: false
								})
							})
						}
					})
					this.chatMessages = messages
					this.$nextTick(this.scrollChatToBottom)
				}
			} catch (e) {
				console.error(e)
				message.error('获取会话详情失败')
			} finally {
				this.chatLoading = false
			}
		},
		renderMath(html) {
			// 使用正则表达式找到所有的数学公式并进行渲染
			// 匹配行内公式和块级公式
			const inlineMathRegex = /\$(.*?)\$/g
			const blockMathRegex = /\[(.*?)\]/g

			// 先处理行内公式
			let result = html.replace(inlineMathRegex, (match, math) => {
				try {
					return `<span class="math">${katex.renderToString(math, {
						displayMode: false
					})}</span>`
				} catch (error) {
					return `<span class="math-error">Error rendering math: ${error.message}</span>`
				}
			})

			// 处理块级公式
			result = result.replace(blockMathRegex, (match, math) => {
				try {
					return `<div class="math-block">${katex.renderToString(math, {
						displayMode: true
					})}</div>`
				} catch (error) {
					return `<div class="math-error">Error rendering math: ${error.message}</div>`
				}
			})

			return result
		},
		parseMarkdown(text) {
			const tmpHtml = marked(text || '', { breaks: true })
			return this.renderMath(tmpHtml)
		}
	}
}
</script>

<style scoped lang="scss">
.pill-wrapper {
	max-width: 1280px;
	margin: 0 auto;
	padding: 96px 20px 60px;
}

.layout {
	display: grid;
	grid-template-columns: 380px 1fr;
	gap: 32px;
	align-items: start;
}

.info-card,
.question-card,
.chat-card {
	border-radius: 20px;
	border: 1.5px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.92);
	backdrop-filter: blur(18px);
	box-shadow: 0 18px 42px rgba(16, 65, 181, 0.18);
}

.info-card {
	padding: 28px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.info-header {
	display: flex;
	align-items: center;
	gap: 18px;
}

.info-avatar {
	width: 92px;
	height: 92px;
	border-radius: 50%;
	object-fit: cover;
	border: 4px solid rgba(94, 123, 243, 0.3);
	box-shadow: 0 10px 24px rgba(94, 123, 243, 0.35);
}

.info-meta {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.info-name {
	margin: 0;
	font-size: 26px;
	font-weight: 700;
	color: #1041b5;
}

.info-subtitle {
	margin: 0;
	font-size: 14px;
	color: #5e7bf3;
}

.tag-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 4px 0;
}

.section-tabs {
	display: flex;
	gap: 12px;
	padding-top: 8px;
	border-top: 1px solid rgba(94, 123, 243, 0.15);
}

.tab-btn {
	flex: 1;
	padding: 10px 12px;
	border: none;
	border-radius: 14px;
	background: rgba(94, 123, 243, 0.1);
	color: #4b5c8f;
	font-size: 14px;
	transition: all 0.2s ease;
	cursor: pointer;
}

.tab-btn.active {
	background: linear-gradient(135deg, #5e7bf3 0%, #1041b5 100%);
	color: #fff;
	box-shadow: 0 10px 28px rgba(94, 123, 243, 0.35);
}

.info-description {
	margin: 8px;
	font-size: 15px;
	line-height: 1.8;
	color: #4b5c8f;
}

.more-info {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 18px;
	border-radius: 16px;
	background: rgba(94, 123, 243, 0.14);
	color: #1041b5;
	font-size: 14px;
}

.question-card {
	margin-top: 20px;
	padding: 24px;
}

.question-header h3 {
	margin: 0 0 6px;
	font-size: 18px;
	color: #1041b5;
}

.question-header p {
	margin: 0 0 18px;
	font-size: 13px;
	color: #5b6a92;
}

.question-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.question-item {
	justify-content: flex-start;
	border: 1px solid rgba(94, 123, 243, 0.3);
	background: linear-gradient(90deg, rgba(149, 142, 246, 0.12) 0%, rgba(94, 123, 243, 0.12) 100%);
	color: #1041b5;
}

.chat-card {
	padding: 28px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.chat-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
}

.chat-header h2 {
	margin: 0;
	font-size: 24px;
	color: #1041b5;
}

.chat-header p {
	margin: 8px 0 0;
	font-size: 14px;
	color: #5b6a92;
}

.chat-body {
	min-height: 420px;
	max-height: 560px;
	padding-right: 6px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-bottom: 12px;
}

.chat-empty {
	text-align: center;
	color: #5b6a92;
	padding: 120px 20px;
}

.chat-message {
	display: flex;
}

.chat-message.user {
	justify-content: flex-end;
}

.chat-message.assistant {
	justify-content: flex-start;
}

.bubble {
	max-width: 80%;
	padding: 14px 18px;
	border-radius: 16px;
	background: rgba(94, 123, 243, 0.08);
	color: #2d3a5f;
	line-height: 1.7;
	position: relative;
}

.chat-message.user .bubble {
	background: linear-gradient(135deg, #5e7bf3 0%, #1041b5 100%);
	color: #fff;
}

.chat-text {
	margin: 0;
	white-space: pre-wrap;
	/* Markdown generated paragraphs (<p>) have a default bottom margin (1em).
	With pre-wrap and p margin combined the bottom gap in chat bubbles
	becomes too large. Reduce per-paragraph bottom margin and remove it
	for the last paragraph to avoid extra whitespace. */
}

/* Reduce spacing for paragraphs inside chat content and remove trailing gap */
.chat-text ::v-deep p {
	margin: 0 0 0.6em; /* keep readable paragraph spacing for v-html content */
}
.chat-text ::v-deep p:last-child {
	margin-bottom: -1.6em; /* no extra gap at the bottom of the bubble for v-html content */
}

.reasoning-section {
	margin-bottom: 12px;
	border: 1px solid rgba(94, 123, 243, 0.2);
	border-radius: 8px;
	overflow: hidden;
}

.reasoning-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	background: rgba(94, 123, 243, 0.08);
	cursor: pointer;
	transition: background 0.2s;
	user-select: none;
}

.reasoning-header:hover {
	background: rgba(94, 123, 243, 0.12);
}

.reasoning-title {
	font-size: 12px;
	font-weight: 600;
	color: #1041b5;
}

.reasoning-toggle {
	font-size: 11px;
	color: #5e7bf3;
}

.reasoning-content {
	padding: 8px 12px;
	background: rgba(94, 123, 243, 0.04);
}

.chat-reason {
	margin: 0;
	font-size: 12px;
	line-height: 1.6;
	color: rgba(16, 65, 181, 0.75);
	white-space: pre-wrap;
}

.chat-loading {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 12px;
	font-size: 13px;
	color: #5b6a92;
}

.chat-error {
	margin-top: 12px;
	font-size: 13px;
	color: #ff4d4f;
}

.chat-input {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.chat-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}

.chat-tip {
	margin: 0;
	font-size: 12px;
	color: #6c7cab;
	text-align: right;
}

@media (max-width: 1100px) {
	.layout {
		grid-template-columns: 1fr;
	}

	.chat-body {
		max-height: none;
		min-height: 380px;
	}

	.bubble {
		max-width: 100%;
	}
}

@media (max-width: 768px) {
	.pill-wrapper {
		padding: 80px 16px 40px;
	}

	.info-card,
	.question-card,
	.chat-card {
		padding: 24px;
	}

	.info-header {
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.tag-list {
		justify-content: center;
	}

	.section-tabs {
		flex-direction: column;
	}

	.chat-actions {
		flex-direction: column;
		align-items: stretch;
	}

	.chat-actions .ant-btn {
		width: 100%;
	}

	.chat-tip {
		text-align: center;
	}
}

:deep(.ant-card-body) {
	padding: 0;
}

:deep(.ant-tag) {
	margin-right: 0;
}

.chat-header-right {
	display: flex;
	align-items: center;
	gap: 12px;
}

.history-btn {
	color: #5e7bf3;
	display: flex;
	align-items: center;
	gap: 4px;
}

.history-btn:hover {
	color: #1041b5;
	background: rgba(94, 123, 243, 0.1);
}

.topic-content {
	padding: 20px;
}

.topic-content .topic-group {
	margin-bottom: 16px; /* spacing between groups inside a-spin */
}

.topic-content .title {
	padding-left: 12px;
	color: #323232;
	font-size: 12px;
	font-weight: 700;
	margin-bottom: 12px;
	margin-top: 24px;
}

.topic-content div:first-child .title {
	margin-top: 0;
}

.topic {
	position: relative;
	color: #323232;
	font-size: 14px;
	margin-bottom: 0px;
	padding: 10px 12px 10px 12px;
	border-radius: 36px;
	width: 100%;
	background: unset;
	text-align: left;
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
	transition: background 0.2s;
}

.topic:hover {
	background: rgba(207, 216, 242, 0.35);
}

.topic.active {
	background: rgba(207, 216, 242, 0.35);
}

.topic span {
	padding-right: 34px;
	display: block;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	flex: 1;
}

.topic .delete-btn {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	padding: 0;
	margin: 0;
	border: none;
	background: unset;
	display: none;
	color: #999;
}

.topic:hover .delete-btn,
.topic.active .delete-btn {
	display: block;
}

.topic .delete-btn:hover {
	color: #ff4d4f;
}

.history-empty {
	text-align: center;
	color: #999;
	padding: 40px 0;
}
</style>
